import React from 'react';
import {
    Modal,
    Box,
    Typography,
    Button,
    Stack,
    CircularProgress
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import CancelIcon from '@mui/icons-material/Cancel';
import { SponsorListItem } from '../../data/sponsorInfo';
import { deleteSponsor } from '../../services/SponsorService';

interface DeleteSponsorModalProps {
    isOpen: boolean;
    onClose: () => void;
    sponsor: SponsorListItem | null;
    eventId: string;
    refreshSponsors: () => Promise<void>;
    setSnackbarDeleteOpen: () => void;
    setDrawerOpen: (open: boolean) => void;
    setSelectedRow: (sponsor: SponsorListItem | null) => void;
}

const DeleteSponsorModal: React.FC<DeleteSponsorModalProps> =
    ({
       isOpen,
       onClose,
       sponsor,
       eventId,
       refreshSponsors,
       setSnackbarDeleteOpen,
       setDrawerOpen,
       setSelectedRow
   }) => {
    const [isDeleting, setIsDeleting] = React.useState(false);
    const [error, setError] = React.useState<string | null>(null);

    const handleDelete = async () => {
        if (!sponsor) return;

        setIsDeleting(true);
        setError(null);

        try {
            await deleteSponsor(sponsor.SponsorId, eventId);

            onClose();
            setDrawerOpen(false);
            await refreshSponsors();
            setSnackbarDeleteOpen();
            setSelectedRow(null);
        } catch (error: any) {
            console.error("Error deleting sponsor:", error);
            let errorMessage = "Failed to delete sponsor";

            if (error.message.includes("Unauthorized")) {
                errorMessage = "You are not authorized to delete sponsors";
            } else if (error.message.includes("not found")) {
                errorMessage = "Sponsor not found";
            }

            setError(errorMessage);
        } finally {
            setIsDeleting(false);
        }
    };

    const modalStyle = {
        position: 'absolute' as 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: 400,
        bgcolor: 'background.paper',
        boxShadow: 24,
        p: 4,
        borderRadius: 2,
    };

    return (
        <Modal
            open={isOpen}
            onClose={isDeleting ? undefined : onClose}
            aria-labelledby="delete-sponsor-modal-title"
        >
            <Box sx={modalStyle}>
                <Typography id="delete-sponsor-modal-title" variant="h6" component="h2" gutterBottom>
                    Delete Sponsor
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                    Are you sure you want to delete {sponsor?.Name}? This action cannot be undone.
                </Typography>

                {error && (
                    <Typography color="error" sx={{ mb: 2 }}>
                        {error}
                    </Typography>
                )}

                <Stack direction="row" spacing={2} justifyContent="flex-end">
                    <Button
                        variant="outlined"
                        startIcon={<CancelIcon />}
                        onClick={onClose}
                        disabled={isDeleting}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        className="delete"
                        color="error"
                        startIcon={isDeleting ? <CircularProgress size={20} color="inherit" /> : <DeleteIcon />}
                        onClick={handleDelete}
                        disabled={isDeleting}
                    >
                        {isDeleting ? 'Deleting...' : 'Delete'}
                    </Button>
                </Stack>
            </Box>
        </Modal>
    );
};

export default DeleteSponsorModal;